import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  getSupportMakerQueue,
  getSupportCheckerQueue,
  actAsSupportMaker,
  actAsSupportChecker
} from "../../services/merchant";
import ProfileNav from "../../components/ProfileNav";

const ApproveMsc = () => {
  const [activeTab, setActiveTab] = useState("maker"); // 'maker' or 'checker'
  const [queueCache, setQueueCache] = useState({}); // { maker: [...], checker: [...] }
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [declineReason, setDeclineReason] = useState({});
  const [declineErrors, setDeclineErrors] = useState({});
  const [busyRows, setBusyRows] = useState({});
  const [modalAction, setModalAction] = useState(null); 
  // { type: 'approve' | 'decline', item: object, reason: string, error: string, isSubmitting: boolean }

  // Derived: requests for the active tab (read from cache)
  const requests = queueCache[activeTab] ?? [];

  const getPayload = (item) => {
    if (!item) return {};
    if (item.payload && typeof item.payload === "object") return item.payload;
    if (typeof item.payload === "string") {
      try {
        return JSON.parse(item.payload);
      } catch (e) {
        console.error("Failed to parse item.payload:", item.payload, e);
      }
    }
    if (item.payloadJson && typeof item.payloadJson === "object") return item.payloadJson;
    if (typeof item.payloadJson === "string") {
      try {
        return JSON.parse(item.payloadJson);
      } catch (e) {
        console.error("Failed to parse item.payloadJson:", item.payloadJson, e);
      }
    }
    return {};
  };

  const loadQueue = useCallback(async (tab) => {
    // Skip fetch if data already cached for this tab
    if (queueCache[tab] !== undefined) return;

    setLoading(true);
    try {
      let data = [];
      if (tab === "maker") {
        data = await getSupportMakerQueue();
      } else {
        data = await getSupportCheckerQueue();
      }
      const list = Array.isArray(data) ? data : data?.data || [];
      setQueueCache(prev => ({ ...prev, [tab]: list }));
    } catch (error) {
      console.error("Failed to fetch queue:", error);
      toast.error("Failed to load request queue.");
      setQueueCache(prev => ({ ...prev, [tab]: [] }));
    } finally {
      setLoading(false);
    }
  }, [queueCache]);

  useEffect(() => {
    loadQueue(activeTab);
    setExpandedRows({});
    setDeclineReason({});
    setDeclineErrors({});
    setBusyRows({});
  }, [activeTab]); // intentionally omit loadQueue to avoid re-running when cache updates

  const openApproveModal = (item) => {
    setModalAction({
      type: "approve",
      item,
      reason: "",
      error: "",
      isSubmitting: false,
    });
  };

  const openDeclineModal = (item, initialReason = "") => {
    const reqId = item.requestId ?? item.id;
    setModalAction({
      type: "decline",
      item,
      reason: initialReason || declineReason[reqId] || "",
      error: "",
      isSubmitting: false,
    });
  };

  const closeModal = () => {
    if (modalAction?.isSubmitting) return;
    setModalAction(null);
  };

  const handleConfirmAction = async () => {
    if (!modalAction || !modalAction.item) return;
    const { type, item, reason } = modalAction;
    const reqId = item.requestId ?? item.id;

    if (type === "decline") {
      const trimmedReason = reason?.trim();
      if (!trimmedReason) {
        setModalAction(prev => ({ ...prev, error: "A reason is required to decline this request." }));
        return;
      }
    }

    setModalAction(prev => ({ ...prev, isSubmitting: true, error: "" }));
    setBusyRows(prev => ({ ...prev, [reqId]: type === "approve" ? "approving" : "declining" }));

    try {
      let res;
      if (type === "approve") {
        const payload = {
          requestId: Number(reqId),
          isApproved: true,
        };
        res = activeTab === "maker" 
          ? await actAsSupportMaker(payload) 
          : await actAsSupportChecker(payload);
        toast.success(res.message || "Request approved successfully!");
      } else {
        const payload = {
          requestId: Number(reqId),
          isApproved: false,
          reason: reason.trim(),
        };
        res = activeTab === "maker" 
          ? await actAsSupportMaker(payload) 
          : await actAsSupportChecker(payload);
        toast.success(res.message || "Request declined successfully!");
      }

      // Remove from the cache for this tab
      setQueueCache(prev => ({
        ...prev,
        [activeTab]: (prev[activeTab] ?? []).filter(r => (r.requestId ?? r.id) !== reqId)
      }));

      setModalAction(null);
    } catch (error) {
      console.error(`${type} error:`, error);
      const errMsg = error?.response?.data?.message || `Failed to ${type} request.`;
      toast.error(errMsg);
      setModalAction(prev => ({ ...prev, isSubmitting: false, error: errMsg }));
    } finally {
      setBusyRows(prev => {
        const copy = { ...prev };
        delete copy[reqId];
        return copy;
      });
    }
  };

  const toggleExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fmtDate = (iso) => {
    if (!iso) return "N/A";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return (
        d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      );
    } catch (e) {
      return iso;
    }
  };

  const fmtFee = (p) => {
    if (!p) return "N/A";
    if (p.feeType === 'Percentage') {
      return `${p.feeValue ?? 0}%`;
    }
    if (p.feeType === 'Flat') {
      return `${p.currencyCode || ''} ${Number(p.feeValue || 0).toLocaleString()} (Flat)`;
    }
    return p.feeValue !== undefined ? `${p.feeValue}` : "N/A";
  };

  const fmtCap = (p) => {
    if (!p) return "None";
    if (p.maxFeeCap !== null && p.maxFeeCap !== undefined && p.maxFeeCap !== "") {
      return `${p.currencyCode || ''} ${Number(p.maxFeeCap).toLocaleString()}`;
    }
    return 'None';
  };

  return (
    <div className="h-screen bg-[#f8fafc] overflow-y-auto">
      <style>
        {`
          :root {
            --payloft-blue: #1e9ee0;
            --payloft-orange: #f5821f;
            --ink: #1b2430;
            --ink-soft: #5b6472;
            --border: #e6e9ee;
            --white: #ffffff;
            --danger: #e5484d;
            --success: #1fa971;
            --shadow: 0 10px 40px rgba(20,30,50,.14);
          }

          .approve-msc-main {
            padding: 32px 40px;
            max-width: 1400px;
            margin: 0 auto;
            text-align: left;
          }

          .msc-topbar {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .msc-page-title {
            font-size: 28px;
            font-weight: 800;
            margin: 0;
            color: var(--ink);
          }

          .msc-page-sub {
            color: var(--ink-soft);
            font-size: 14px;
            margin-top: 4px;
          }

          .msc-datepill {
            border: 1px solid var(--border);
            background: #fff;
            border-radius: 8px;
            padding: 8px 14px;
            font-size: 13px;
            color: var(--ink-soft);
            white-space: nowrap;
          }

          .msc-role-switch {
            display: inline-flex;
            border: 1px solid var(--border);
            border-radius: 9px;
            background: #fff;
            padding: 3px;
            margin-top: 18px;
          }

          .msc-role-switch button {
            border: none;
            background: transparent;
            padding: 8px 16px;
            border-radius: 7px;
            font-size: 13.5px;
            font-weight: 700;
            color: var(--ink-soft);
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .msc-role-switch button.active {
            background: var(--payloft-blue);
            color: #fff;
          }

          .msc-role-note {
            font-size: 12.5px;
            color: var(--ink-soft);
            margin-top: 8px;
          }

          .msc-queue-wrap {
            background: #fff;
            border: 1px solid var(--border);
            border-radius: 12px;
            margin-top: 22px;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          }

          .msc-queue-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 18px 22px;
            border-bottom: 1px solid var(--border);
          }

          .msc-queue-head h2 {
            margin: 0;
            font-size: 17px;
            font-weight: 800;
            color: var(--ink);
          }

          .msc-badge-count {
            background: #fff3ea;
            color: var(--payloft-orange);
            font-size: 12px;
            font-weight: 700;
            padding: 3px 10px;
            border-radius: 999px;
            margin-left: 8px;
          }

          .msc-table {
            width: 100%;
            border-collapse: collapse;
          }

          .msc-table thead th {
            text-align: left;
            font-size: 11.5px;
            text-transform: uppercase;
            letter-spacing: .04em;
            color: var(--ink-soft);
            padding: 12px 22px;
            border-bottom: 1px solid var(--border);
            font-weight: 700;
            background: #fbfcfd;
          }

          .msc-table tbody tr.row-item {
            border-bottom: 1px solid #f1f2f4;
            transition: background .15s ease;
            cursor: pointer;
          }

          .msc-table tbody tr.row-item:hover {
            background: #fafbfc;
          }

          .msc-table td {
            padding: 14px 22px;
            font-size: 13.5px;
            vertical-align: top;
            color: var(--ink);
          }

          .msc-table td.merchant {
            font-weight: 700;
          }

          .msc-table td .sub {
            color: var(--ink-soft);
            font-size: 12px;
            margin-top: 2px;
            font-weight: normal;
          }

          .msc-pill {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 3px 10px;
            border-radius: 999px;
            font-size: 11.5px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .02em;
          }

          .msc-pill.level1 { background: #fff3ea; color: var(--payloft-orange); }
          .msc-pill.level2 { background: #eef7fe; color: var(--payloft-blue); }
          .msc-pill.customer { background: #eafaf3; color: #0d7a52; }
          .msc-pill.merchantpay { background: #f2f4f6; color: var(--ink-soft); }
          .msc-pill.currency { background: #f0f4ff; color: #3b5bdb; }

          .msc-row-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
          }

          .msc-icon-btn {
            border: 1px solid var(--border);
            background: #fff;
            border-radius: 7px;
            padding: 7px 12px;
            font-size: 12.5px;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.15s ease;
          }

          .msc-icon-btn.approve {
            color: var(--success);
            border-color: #bfeeda;
          }

          .msc-icon-btn.approve:hover {
            background: #eafaf3;
          }

          .msc-icon-btn.decline {
            color: var(--danger);
            border-color: #f5c8c9;
          }

          .msc-icon-btn.decline:hover {
            background: #fdeeee;
          }

          .msc-icon-btn:disabled {
            opacity: .4;
            cursor: not-allowed;
          }

          .msc-detail-row td {
            background: #fbfcfd;
            padding: 0;
          }

          .msc-detail-body {
            padding: 22px;
            border-bottom: 1px solid var(--border);
          }

          .msc-kv-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 16px;
          }

          .msc-kv {
            font-size: 13.5px;
          }

          .msc-kv .label {
            color: var(--ink-soft);
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: .03em;
            margin-bottom: 4px;
            font-weight: bold;
          }

          .msc-kv .value {
            font-weight: 700;
            color: var(--ink);
          }

          .msc-decline-box {
            background: #fff;
            border: 1px solid var(--border);
            border-radius: 8px;
            padding: 14px;
            margin-top: 10px;
          }

          .msc-decline-box label {
            font-size: 12px;
            font-weight: 700;
            color: var(--ink-soft);
            text-transform: uppercase;
            letter-spacing: .03em;
            display: block;
            margin-bottom: 6px;
          }

          .msc-decline-box textarea {
            width: 100%;
            border: 1px solid var(--border);
            border-radius: 7px;
            padding: 10px 12px;
            font-size: 13.5px;
            font-family: inherit;
            resize: vertical;
            min-height: 64px;
            color: var(--ink);
          }

          .msc-decline-box textarea:focus {
            outline: none;
            border-color: var(--danger);
            box-shadow: 0 0 0 3px rgba(229,72,77,.12);
          }

          .msc-decline-box .err {
            color: var(--danger);
            font-size: 12px;
            margin-top: 5px;
            display: none;
          }

          .msc-decline-box.invalid .err {
            display: block;
          }

          .msc-decline-box .btnrow {
            display: flex;
            gap: 8px;
            margin-top: 10px;
            justify-content: flex-end;
          }

          .msc-btn {
            border: none;
            border-radius: 8px;
            padding: 9px 16px;
            font-size: 13.5px;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 7px;
            transition: all 0.2s ease;
          }

          .msc-btn-danger {
            background: var(--danger);
            color: #fff;
          }

          .msc-btn-danger:hover {
            background: #cf3b40;
          }

          .msc-btn-ghost {
            background: #fff;
            border: 1px solid var(--border);
            color: var(--ink);
          }

          .msc-btn-ghost:hover {
            background: #f7f8fa;
          }

          .msc-spinner {
            width: 13px;
            height: 13px;
            border-radius: 50%;
            border: 2px solid rgba(255,255,255,.4);
            border-top-color: #fff;
            animation: msc-spin-anim .7s linear infinite;
            display: inline-block;
          }

          .msc-spinner.dark {
            border: 2px solid rgba(30,40,60,.15);
            border-top-color: var(--ink-soft);
          }

          @keyframes msc-spin-anim {
            to { transform: rotate(360deg); }
          }

          .msc-empty-state {
            text-align: center;
            padding: 64px 20px;
          }

          .msc-empty-state .emoji {
            font-size: 36px;
            margin-bottom: 10px;
          }

          .msc-empty-state h3 {
            margin: 0 0 6px;
            font-size: 16px;
            font-weight: bold;
            color: var(--ink);
          }

          .msc-empty-state p {
            color: var(--ink-soft);
            font-size: 13.5px;
            margin: 0;
          }

          .msc-skeleton-row td {
            padding: 16px 22px;
          }

          .msc-skeleton {
            height: 13px;
            border-radius: 5px;
            background: linear-gradient(90deg,#eef0f2,#f6f7f8,#eef0f2);
            background-size: 200% 100%;
            animation: msc-shimmer 1.1s infinite;
          }

          @keyframes msc-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .msc-arrow {
            display: inline-block;
            transition: transform 0.2s ease;
            font-size: 12px;
            color: var(--ink-soft);
          }
          .msc-arrow.expanded {
            transform: rotate(90deg);
          }

          @keyframes mscFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes mscScaleUp {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }

          .msc-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.6);
            backdrop-filter: blur(4px);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            animation: mscFadeIn 0.2s ease-out;
          }

          .msc-modal-card {
            background: #ffffff;
            border-radius: 16px;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            width: 100%;
            max-width: 480px;
            overflow: hidden;
            border: 1px solid var(--border);
            animation: mscScaleUp 0.2s cubic-bezier(0.16, 1, 0.3, 1);
            text-align: left;
          }

          .msc-modal-header {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 20px 24px;
            border-bottom: 1px solid var(--border);
            background: #fcfdfe;
          }

          .msc-modal-icon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 900;
            flex-shrink: 0;
          }

          .msc-modal-icon.approve {
            background: #eafaf3;
            color: var(--success);
          }

          .msc-modal-icon.decline {
            background: #fdeeee;
            color: var(--danger);
          }

          .msc-modal-title {
            margin: 0;
            font-size: 17px;
            font-weight: 800;
            color: var(--ink);
          }

          .msc-modal-subtitle {
            margin: 3px 0 0 0;
            font-size: 12.5px;
            color: var(--ink-soft);
          }

          .msc-modal-body {
            padding: 22px 24px;
          }

          .msc-modal-summary {
            background: #f8fafc;
            border: 1px solid var(--border);
            border-radius: 10px;
            padding: 12px 16px;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px 16px;
            margin-bottom: 18px;
          }

          .msc-summary-item {
            display: flex;
            flex-direction: column;
          }

          .msc-summary-item .label {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .03em;
            color: var(--ink-soft);
          }

          .msc-summary-item .val {
            font-size: 13.5px;
            font-weight: 700;
            color: var(--ink);
            margin-top: 2px;
          }

          .msc-modal-note {
            padding: 12px 14px;
            border-radius: 8px;
            font-size: 13px;
            line-height: 1.45;
          }

          .msc-modal-note.approve {
            background: #f0f7ff;
            border: 1px solid #cfe5ff;
            color: #1e429f;
          }

          .msc-modal-form-group label {
            display: block;
            font-size: 12px;
            font-weight: 700;
            color: var(--ink);
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: .02em;
          }

          .msc-modal-form-group label .req {
            color: var(--danger);
            text-transform: none;
            font-weight: 500;
          }

          .msc-modal-form-group textarea {
            width: 100%;
            border: 1px solid var(--border);
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 13.5px;
            font-family: inherit;
            resize: vertical;
            min-height: 80px;
            color: var(--ink);
            box-sizing: border-box;
          }

          .msc-modal-form-group textarea:focus {
            outline: none;
            border-color: var(--payloft-blue);
            box-shadow: 0 0 0 3px rgba(30, 158, 224, 0.15);
          }

          .msc-modal-error {
            color: var(--danger);
            font-size: 12px;
            margin-top: 6px;
            font-weight: 600;
          }

          .msc-modal-footer {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 12px;
            padding: 16px 24px;
            background: #fbfcfd;
            border-top: 1px solid var(--border);
          }

          .msc-btn-approve {
            background: var(--success);
            color: #fff;
            border: none;
            padding: 10px 20px;
            border-radius: 8px;
            font-weight: 700;
            font-size: 13.5px;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .msc-btn-approve:hover:not(:disabled) {
            background: #178a5b;
          }

          .msc-btn-approve:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}
      </style>

      <div className="approve-msc-main">
        <div className="msc-topbar">
          <div>
            <h1 className="msc-page-title">Approve MSC</h1>
            <div className="msc-page-sub">
              {activeTab === "maker"
                ? "New fee configuration requests waiting for first-level approval."
                : "Maker-approved requests waiting for final sign-off."}
            </div>
          </div>
          <div>
            <ProfileNav />
          </div>
        </div>

        {/* Role Switcher */}
        <div>
          <div className="msc-role-switch">
            <button
              onClick={() => setActiveTab("maker")}
              className={activeTab === "maker" ? "active" : ""}
            >
              Support Maker view
            </button>
            <button
              onClick={() => setActiveTab("checker")}
              className={activeTab === "checker" ? "active" : ""}
            >
              Support Checker view
            </button>
          </div>
          <div className="msc-role-note">
            {activeTab === "maker" ? (
              <>
                Logged in as <b>Support Maker</b>. Approving here sends requests to a Support Checker for final sign-off.
              </>
            ) : (
              <>
                Logged in as <b>Support Checker</b>. Approving here activates the fee configuration immediately.
              </>
            )}
          </div>
        </div>

        {/* Request Queue Table */}
        <div className="msc-queue-wrap">
          <div className="msc-queue-head">
            <h2>
              {activeTab === "maker" ? "Support Maker queue" : "Support Checker queue"}
              <span className="msc-badge-count">{requests.length}</span>
            </h2>
          </div>

          <table className="msc-table">
            <thead>
              <tr>
                <th style={{ width: "26px" }}></th>
                <th>Req ID</th>
                <th>Merchant</th>
                <th>Currency</th>
                <th>Fee</th>
                <th>Charge borne by</th>
                <th>Status</th>
                <th>Submitted</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  <tr className="msc-skeleton-row">
                    <td colSpan="9"><div className="msc-skeleton" style={{ width: "100%" }}></div></td>
                  </tr>
                  <tr className="msc-skeleton-row">
                    <td colSpan="9"><div className="msc-skeleton" style={{ width: "85%" }}></div></td>
                  </tr>
                </>
              )}

              {!loading && requests.length === 0 && (
                <tr>
                  <td colSpan="9" style={{ padding: 0 }}>
                    <div className="msc-empty-state">
                      <div className="emoji">✅</div>
                      <h3>All caught up</h3>
                      <p>
                        {activeTab === "maker"
                          ? "No new MSC requests are pending your review right now."
                          : "No maker-approved requests are waiting on you right now."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && requests.map((item) => {
                const reqId = item.requestId ?? item.id;
                const payload = getPayload(item);
                const isExpanded = !!expandedRows[reqId];
                const isBusy = busyRows[reqId];

                return (
                  <React.Fragment key={reqId}>
                    <tr className="row-item" onClick={() => toggleExpand(reqId)}>
                      <td style={{ verticalAlign: "middle" }}>
                        <span className={`msc-arrow ${isExpanded ? "expanded" : ""}`}>▶</span>
                      </td>
                      <td style={{ verticalAlign: "middle", fontWeight: 700, color: "var(--ink)" }}>
                        #{reqId}
                      </td>
                      <td className="merchant" style={{ verticalAlign: "middle" }}>
                        {item.merchantName || (payload.merchantId ? `Merchant #${payload.merchantId}` : "N/A")}
                        <div className="sub">Merchant ID: {payload.merchantId || item.partyId || "N/A"}</div>
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        <span className="msc-pill currency">{payload.currencyCode || "NGN"}</span>
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        <b>{fmtFee(payload)}</b>
                        <div className="sub">Cap: {fmtCap(payload)}</div>
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        {payload.isCustomerPaying ? (
                          <span className="msc-pill customer">Customer</span>
                        ) : (
                          <span className="msc-pill merchantpay">Merchant</span>
                        )}
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        <span className={`msc-pill level${item.currentLevel || 1}`}>
                          {item.status || "Pending"} (Lvl {item.currentLevel || 1})
                        </span>
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        {fmtDate(item.createdAt)}
                        <div className="sub">by {item.createdBy || "System"}</div>
                      </td>
                      <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                        <div className="msc-row-actions">
                          <button
                            className="msc-icon-btn approve"
                            disabled={!!isBusy}
                            onClick={(e) => {
                              e.stopPropagation();
                              openApproveModal(item);
                            }}
                          >
                            {isBusy === "approving" ? (
                              <>
                                <span className="msc-spinner dark"></span> Approving...
                              </>
                            ) : (
                              "✓ Approve"
                            )}
                          </button>
                          <button
                            className="msc-icon-btn decline"
                            disabled={!!isBusy}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeclineModal(item);
                            }}
                          >
                            ✕ Decline
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="msc-detail-row">
                        <td colSpan="9">
                          <div className="msc-detail-body">
                            <div className="msc-kv-grid">
                              <div className="msc-kv">
                                <div className="label">Request ID</div>
                                <div className="value">#{reqId}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Merchant ID</div>
                                <div className="value">#{payload.merchantId || item.partyId || "N/A"}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Currency</div>
                                <div className="value">{payload.currencyCode || "N/A"}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Fee Type</div>
                                <div className="value">{payload.feeType || "N/A"}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Fee Value</div>
                                <div className="value">{fmtFee(payload)}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Max Cap</div>
                                <div className="value">{fmtCap(payload)}</div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Charge Borne By</div>
                                <div className="value">
                                  {payload.isCustomerPaying ? "Customer" : "Merchant (Absorbed)"}
                                </div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Status & Level</div>
                                <div className="value">
                                  {item.status || "Pending"} · Level {item.currentLevel || 1}
                                </div>
                              </div>
                              <div className="msc-kv">
                                <div className="label">Submitted By</div>
                                <div className="value">{item.createdBy || "N/A"}</div>
                              </div>
                              {payload.scheme && (
                                <div className="msc-kv">
                                  <div className="label">Scheme</div>
                                  <div className="value">{payload.scheme}</div>
                                </div>
                              )}
                            </div>

                            {/* Decline Box */}
                            <div className={`msc-decline-box ${declineErrors[reqId] ? "invalid" : ""}`}>
                              <label>Reason for declining <span style={{ fontWeight: 500, textTransform: "none" }}>(required)</span></label>
                              <textarea
                                value={declineReason[reqId] || ""}
                                onChange={(e) => {
                                  const text = e.target.value;
                                  setDeclineReason(prev => ({ ...prev, [reqId]: text }));
                                }}
                                placeholder="e.g. Fee value looks like a typo — confirm with merchant."
                              />
                              <div className="err">A reason is required to decline this request.</div>
                              <div className="btnrow">
                                <button
                                  className="msc-btn msc-btn-ghost"
                                  onClick={() => {
                                    setDeclineReason(prev => ({ ...prev, [reqId]: "" }));
                                    toggleExpand(reqId);
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="msc-btn msc-btn-danger"
                                  disabled={isBusy === "declining"}
                                  onClick={() => openDeclineModal(item, declineReason[reqId])}
                                >
                                  {isBusy === "declining" ? (
                                    <>
                                      <span className="msc-spinner"></span> Declining...
                                    </>
                                  ) : (
                                    "Confirm decline"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal for Approve & Decline */}
      {modalAction && modalAction.item && (
        <div className="msc-modal-overlay" onClick={closeModal}>
          <div className="msc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="msc-modal-header">
              <div className={`msc-modal-icon ${modalAction.type === 'approve' ? 'approve' : 'decline'}`}>
                {modalAction.type === 'approve' ? '✓' : '✕'}
              </div>
              <div>
                <h3 className="msc-modal-title">
                  {modalAction.type === 'approve' ? 'Approve Fee Configuration' : 'Decline Fee Configuration'}
                </h3>
                <p className="msc-modal-subtitle">
                  Request #{modalAction.item.requestId ?? modalAction.item.id} · Merchant #{getPayload(modalAction.item).merchantId || modalAction.item.partyId || 'N/A'}
                </p>
              </div>
            </div>

            <div className="msc-modal-body">
              {/* Summary card */}
              <div className="msc-modal-summary">
                <div className="msc-summary-item">
                  <span className="label">Currency</span>
                  <span className="val">{getPayload(modalAction.item).currencyCode || 'NGN'}</span>
                </div>
                <div className="msc-summary-item">
                  <span className="label">Fee</span>
                  <span className="val">{fmtFee(getPayload(modalAction.item))}</span>
                </div>
                <div className="msc-summary-item">
                  <span className="label">Cap</span>
                  <span className="val">{fmtCap(getPayload(modalAction.item))}</span>
                </div>
                <div className="msc-summary-item">
                  <span className="label">Charge Borne By</span>
                  <span className="val">
                    {getPayload(modalAction.item).isCustomerPaying ? 'Customer' : 'Merchant'}
                  </span>
                </div>
              </div>

              {modalAction.type === 'approve' ? (
                <div className="msc-modal-note approve">
                  {activeTab === 'maker' ? (
                    <>
                      <b>First-Level Approval:</b> Approving will route this fee configuration request to <b>Support Checker</b> for final sign-off.
                    </>
                  ) : (
                    <>
                      <b>Final Sign-Off:</b> Approving will <b>activate</b> this fee configuration immediately in production.
                    </>
                  )}
                </div>
              ) : (
                <div className="msc-modal-form-group">
                  <label htmlFor="modalDeclineReason">
                    Reason for declining <span className="req">(required)</span>
                  </label>
                  <textarea
                    id="modalDeclineReason"
                    autoFocus
                    value={modalAction.reason}
                    onChange={(e) => {
                      const val = e.target.value;
                      setModalAction(prev => ({ ...prev, reason: val, error: "" }));
                    }}
                    placeholder="e.g. Fee value looks like a typo — confirm with merchant."
                    rows={3}
                  />
                  {modalAction.error && (
                    <div className="msc-modal-error">{modalAction.error}</div>
                  )}
                </div>
              )}
            </div>

            <div className="msc-modal-footer">
              <button
                type="button"
                className="msc-btn msc-btn-ghost"
                disabled={modalAction.isSubmitting}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={modalAction.type === 'approve' ? 'msc-btn-approve' : 'msc-btn msc-btn-danger'}
                disabled={modalAction.isSubmitting}
                onClick={handleConfirmAction}
              >
                {modalAction.isSubmitting ? (
                  <>
                    <span className="msc-spinner"></span> Processing...
                  </>
                ) : modalAction.type === 'approve' ? (
                  "Confirm & Approve"
                ) : (
                  "Confirm Decline"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveMsc;
